import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../Redux/Store/Hooks";
import { getRolesScreenlist } from "../../Redux/Slices/Screen/RolesScreenList";
import { getAddRolesScreen } from "../../Redux/Slices/RoleMapping/AddRolesScreens";
import { IScreen } from "./IScreen";
import { IRoleMapping } from "./IRoleMappingMaster";

interface RoleScreenMappingModalProps {
    open: boolean;
    onClose: () => void;
    roles: IRoleMapping[];
    selectedRoleId?: number;
}

const RoleScreenMappingModal: React.FC<RoleScreenMappingModalProps> = ({
    open,
    onClose,
    roles,
    selectedRoleId,
}) => {
    const dispatch = useDispatch();
    const [roleId, setRoleId] = useState<number>(selectedRoleId || 0);
    const [selectedScreens, setSelectedScreens] = useState<string[]>([]);

    const { data: screensData, isLoading: screensLoading } = useAppSelector(
        (state) => state.RolesScreenList
    );

    const { data: addScreensData, isLoading: addScreensLoading } = useAppSelector(
        (state) => state.AddRolesScreen
    );

    const screens: IScreen[] = screensData?.data || [];
    const loading = screensLoading || false;

    // Fetch screens when role is selected
    useEffect(() => {
        if (roleId > 0) {
            dispatch(getRolesScreenlist({ role_id: roleId }));
        }
    }, [roleId, dispatch]);

    // Update selected screens when screens data changes
    useEffect(() => {
        if (screens.length > 0) {
            const mappedScreenIds = screens
                .filter((screen) => screen.mapped)
                .map((screen) => screen.screen_id);
            setSelectedScreens(mappedScreenIds);
        }
    }, [screens]);

    // Set initial role if provided
    useEffect(() => {
        if (selectedRoleId) {
            setRoleId(selectedRoleId);
        }
    }, [selectedRoleId]);

    const handleRoleChange = (event: any) => {
        setRoleId(event.target.value);
        setSelectedScreens([]);
    };

    const handleScreenToggle = (screenId: string) => {
        setSelectedScreens((prev) => {
            if (prev.includes(screenId)) {
                return prev.filter((id) => id !== screenId);
            } else {
                return [...prev, screenId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedScreens.length === screens.length) {
            setSelectedScreens([]);
        } else {
            setSelectedScreens(screens.map((screen) => screen.screen_id));
        }
    };

    const handleSubmit = () => {
        if (roleId > 0) {
            dispatch(
                getAddRolesScreen({
                    role_id: roleId,
                    screen_ids: selectedScreens as any,
                })
            );
            // Close modal after a short delay to show success
            setTimeout(() => {
                onClose();
            }, 1000);
        }
    };

    const handleClose = () => {
        setRoleId(selectedRoleId || 0);
        setSelectedScreens([]);
        onClose();
    };

    const selectedRole = roles.find((role) => role.role_id === roleId);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Role Screen Mapping
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Role Selection */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Select Role</InputLabel>
                        <Select
                            value={roleId}
                            onChange={handleRoleChange}
                            label="Select Role"
                            disabled
                        >
                            <MenuItem value={0}>
                                <em>Select a role</em>
                            </MenuItem>
                            {roles.map((role) => (
                                <MenuItem key={role.role_id} value={role.role_id}>
                                    {role.role_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {roleId > 0 && (
                        <>
                            <Divider sx={{ mb: 2 }} />

                            {/* Screen Selection Header */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Screens
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={handleSelectAll}
                                    sx={{ textTransform: "none" }}
                                >
                                    {selectedScreens.length === screens.length
                                        ? "Deselect All"
                                        : "Select All"}
                                </Button>
                            </Box>

                            {/* Loading State */}
                            {loading && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight="200px"
                                >
                                    <CircularProgress />
                                </Box>
                            )}

                            {/* Screens List */}
                            {!loading && screens.length > 0 && (
                                <Box
                                    sx={{
                                        maxHeight: "400px",
                                        overflowY: "auto",
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    {screens.map((screen) => (
                                        <FormControlLabel
                                            key={screen.screen_id}
                                            control={
                                                <Checkbox
                                                    checked={selectedScreens.includes(
                                                        screen.screen_id
                                                    )}
                                                    onChange={() =>
                                                        handleScreenToggle(screen.screen_id)
                                                    }
                                                    sx={{
                                                        color: "#00bcd4",
                                                        "&.Mui-checked": {
                                                            color: "#00bcd4",
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography fontWeight={500}>
                                                        {screen.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {screen.path}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{
                                                width: "100%",
                                                mb: 1,
                                                "&:hover": {
                                                    backgroundColor: "#f5f5f5",
                                                },
                                                borderRadius: 1,
                                                p: 1,
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* Empty State */}
                            {!loading && screens.length === 0 && (
                                <Alert severity="info">
                                    No screens available for this role.
                                </Alert>
                            )}

                            {/* Selected Count */}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 2 }}
                            >
                                Selected: {selectedScreens.length} / {screens.length}
                            </Typography>
                        </>
                    )}

                    {/* Success Message */}
                    {addScreensData?.status && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {addScreensData?.message || "Screens updated successfully!"}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        roleId === 0 ||
                        addScreensLoading ||
                        selectedRole?.role_name?.toLowerCase() === "admin"
                    }
                    sx={{
                        textTransform: "none",
                        backgroundColor: "#00bcd4",
                        "&:hover": {
                            backgroundColor: "#00acc1",
                        },
                    }}
                >
                    {addScreensLoading ? <CircularProgress size={24} /> : "Submit"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleScreenMappingModal;
